import React, { useState } from "react";
import { esgApi } from "../services/api";
import { validateFile, validateImageFile } from "../config/esgQuestions";

/**
 * Componente per l'upload di file con presigned URL
 * @param {object} props
 * @param {string} props.questionKey - Chiave stabile della domanda
 * @param {string} props.label - Label da mostrare
 * @param {function} props.onUploadComplete - Callback quando upload completo (attachmentData)
 * @param {function} props.onUploadError - Callback in caso di errore
 * @param {object} props.existingAttachment - Allegato esistente se presente
 * @param {string} props.fileType - Tipo di file ("file" o "image") - default: "file"
 * @param {string} props.description - Descrizione aggiuntiva dei file accettati
 */
const FileUploader = ({
  questionKey,
  label,
  onUploadComplete,
  onUploadError,
  existingAttachment,
  fileType = "file",
  description,
}) => {
  const isCompanyLogo = questionKey === "q11_company_logo";
  const [uploadState, setUploadState] = useState("idle"); // idle | uploading | success | error
  const [progress, setProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null); // Per anteprima immagini

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset stato
    setErrorMessage("");
    setUploadState("idle");
    setSelectedFile(file);
    setPreview(null);

    // Valida il file
    const validator = fileType === "image" ? validateImageFile : validateFile;
    const validation = validator(file);
    if (!validation.isValid) {
      setErrorMessage(validation.error);
      setUploadState("error");
      if (onUploadError) {
        onUploadError(questionKey, validation.error);
      }
      return;
    }

    // Company logo must be raster-friendly for downstream PDF rendering.
    if (isCompanyLogo) {
      const fileExtension = file.name.includes(".")
        ? file.name.slice(file.name.lastIndexOf(".")).toLowerCase()
        : "";
      const allowedMimeTypes = ["image/png", "image/jpeg"];
      const allowedExtensions = [".png", ".jpg", ".jpeg"];

      if (!allowedMimeTypes.includes(file.type) || !allowedExtensions.includes(fileExtension)) {
        const errMsg = "Logo must be PNG or JPEG (.png, .jpg, .jpeg)";
        setErrorMessage(errMsg);
        setUploadState("error");
        if (onUploadError) {
          onUploadError(questionKey, errMsg);
        }
        return;
      }
    }

    // Crea anteprima per immagini
    if (fileType === "image" && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setPreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }

    // Inizia l'upload
    await uploadFile(file);
  };

  const uploadFile = async (file) => {
    try {
      setUploadState("uploading");
      setProgress(0);

      // 1) Richiedi presigned URL al backend
      const { uploadUrl, storageKey } = await esgApi.getPresignedUploadUrl({
        fileName: file.name,
        contentType: file.type,
        size: file.size,
        questionKey,
      });

      // 2) Upload diretto a R2 con progress
      await esgApi.uploadFileToR2(uploadUrl, file, (percent) => {
        setProgress(percent);
      });

      // 3) Salva i dati dell'allegato
      const attachmentData = {
        questionKey,
        fileName: file.name,
        mimeType: file.type,
        contentType: file.type,
        size: file.size,
        storageKey,
      };

      setUploadState("success");
      setProgress(100);

      if (onUploadComplete) {
        onUploadComplete(questionKey, attachmentData);
      }
    } catch (error) {
      console.error("Upload error:", error);
      const errMsg = error.message || "Upload failed";
      setErrorMessage(errMsg);
      setUploadState("error");

      if (onUploadError) {
        onUploadError(questionKey, errMsg);
      }
    }
  };

  const handleRetry = () => {
    if (selectedFile) {
      uploadFile(selectedFile);
    }
  };

  const handleRemove = () => {
    setSelectedFile(null);
    setUploadState("idle");
    setProgress(0);
    setErrorMessage("");
    setPreview(null);
    if (onUploadComplete) {
      onUploadComplete(questionKey, null);
    }
  };

  return (
    <div style={styles.container}>
      <label style={styles.label}>{label}</label>

      {/* Informazioni file accettati */}
      <div style={styles.infoBox}>
        <span style={styles.infoText}>
          {fileType === "image"
            ? (isCompanyLogo
              ? "📁 Accepted: JPG, PNG (max 5MB)"
              : "📁 Accepted: JPG, PNG, SVG (max 5MB)")
            : "📁 Accepted: PDF, JPG, PNG, DOCX, XLSX (max 20MB)"}
        </span>
        {description && <span style={styles.descriptionText}>{description}</span>}
      </div>

      {/* File input */}
      <input
        type="file"
        accept={
          fileType === "image"
            ? (isCompanyLogo ? ".jpg,.jpeg,.png" : ".jpg,.jpeg,.png,.svg")
            : ".pdf,.jpg,.jpeg,.png,.docx,.xlsx"
        }
        onChange={handleFileSelect}
        style={styles.fileInput}
        disabled={uploadState === "uploading"}
      />

      {/* Anteprima immagine */}
      {(preview || existingAttachment) && fileType === "image" && uploadState !== "error" && (
        <div style={styles.previewContainer}>
          {preview && (
            <img src={preview} alt="Logo preview" style={styles.previewImage} />
          )}
        </div>
      )}

      {/* Stato upload */}
      {uploadState === "uploading" && (
        <div style={styles.statusContainer}>
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: `${progress}%` }} />
          </div>
          <span style={styles.statusText}>Uploading... {progress}%</span>
        </div>
      )}

      {uploadState === "success" && (
        <div style={styles.statusContainer}>
          <div style={styles.successContent}>
            <span style={{ ...styles.statusText, color: "#27ae60", fontWeight: "500" }}>
              ✓ {selectedFile?.name || existingAttachment?.fileName} uploaded successfully
            </span>
            {fileType === "image" && preview && (
              <img src={preview} alt="Logo preview" style={styles.successPreviewImage} />
            )}
          </div>
          <button onClick={handleRemove} style={styles.removeButton}>
            Remove
          </button>
        </div>
      )}

      {uploadState === "error" && (
        <div style={styles.statusContainer}>
          <div style={styles.errorContent}>
            <span style={{ ...styles.statusText, color: "#e74c3c", fontWeight: "500" }}>
              ✗ {errorMessage}
            </span>
          </div>
          <div style={styles.buttonGroup}>
            <button onClick={handleRetry} style={styles.retryButton}>
              Retry
            </button>
            <button onClick={handleRemove} style={styles.removeButton}>
              Remove
            </button>
          </div>
        </div>
      )}

      {existingAttachment && uploadState === "idle" && !selectedFile && (
        <div style={styles.statusContainer}>
          <span style={{ ...styles.statusText, color: "#3498db" }}>
            📎 {existingAttachment.fileName}
          </span>
          <button onClick={handleRemove} style={styles.removeButton}>
            Remove
          </button>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    marginBottom: "20px",
  },
  label: {
    display: "block",
    marginBottom: "8px",
    fontWeight: "600",
    color: "#2c3e50",
    fontSize: "15px",
  },
  infoBox: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    marginBottom: "10px",
    padding: "10px 12px",
    background: "rgba(15,23,42,0.8)",
    borderRadius: "6px",
    border: "1px solid rgba(148,163,184,0.35)",
    boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.02)",
  },
  infoText: {
    fontSize: "13px",
    color: "#dbeafe",
    fontWeight: "500",
  },
  descriptionText: {
    fontSize: "12px",
    color: "#94a3b8",
    fontStyle: "italic",
  },
  fileInput: {
    display: "block",
    width: "100%",
    padding: "10px 8px",
    border: "2px solid #3498db",
    borderRadius: "4px",
    fontSize: "14px",
    cursor: "pointer",
    backgroundColor: "#f8f9fa",
    transition: "all 0.2s ease",
  },
  previewContainer: {
    marginTop: "12px",
    padding: "10px",
    backgroundColor: "#f8f9fa",
    borderRadius: "4px",
    border: "1px solid #e0e0e0",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  previewImage: {
    maxWidth: "200px",
    maxHeight: "200px",
    borderRadius: "4px",
    objectFit: "contain",
  },
  successPreviewImage: {
    width: "80px",
    height: "80px",
    borderRadius: "4px",
    objectFit: "contain",
    marginLeft: "10px",
  },
  statusContainer: {
    marginTop: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    padding: "10px 12px",
    backgroundColor: "#f8f9fa",
    borderRadius: "4px",
  },
  successContent: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flex: 1,
  },
  errorContent: {
    flex: 1,
  },
  statusText: {
    fontSize: "14px",
    color: "#7f8c8d",
  },
  progressBar: {
    flex: 1,
    height: "8px",
    backgroundColor: "#ecf0f1",
    borderRadius: "4px",
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    backgroundColor: "#3498db",
    transition: "width 0.3s ease",
  },
  buttonGroup: {
    display: "flex",
    gap: "8px",
  },
  retryButton: {
    padding: "6px 12px",
    fontSize: "14px",
    backgroundColor: "#f39c12",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontWeight: "500",
    transition: "background-color 0.2s ease",
  },
  removeButton: {
    padding: "6px 12px",
    fontSize: "14px",
    backgroundColor: "#e74c3c",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontWeight: "500",
    transition: "background-color 0.2s ease",
  },
};

export default FileUploader;
